import React, { useCallback, useMemo, useState } from 'react';
import { BarChart3, DollarSign, FileText, TrendingUp, Wallet } from 'lucide-react';
import AppLoader from '../components/modules/AppLoader';
import ModuleAlert from '../components/modules/ModuleAlert';
import ModulePageHeader from '../components/modules/ModulePageHeader';
import apiService, {
  extractCommissionHistoryList,
  extractCommissionHistoryPagination,
} from '../utils/apiService';
import { useFetchOnceByKey } from '../hooks/useMountOnce';
import { pick, unwrapData, unwrapList } from '../utils/moduleHelpers';
import { getThemeColor } from '../config/appConfig';
import '../styles/module-shared.css';
import './reports/commission-report.css';

const THEME = getThemeColor('primary');
const PAGE_SIZE = 10;

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(amount) || 0);

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const toApiDate = (dateStr) => {
  if (!dateStr) return '';
  // Keep calendar day as-is. Avoid toISOString() UTC day-shift (UTC+ zones).
  // API inclusive end: ToDate.Date.AddDays(1).
  const match = String(dateStr).trim().match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? `${match[1]}T00:00:00` : '';
};

const normalizeMonthRows = (payload) => {
  const list =
    unwrapList({ data: payload }) ||
    unwrapList({ data: payload?.result }) ||
    unwrapList({ data: payload?.data }) ||
    [];
  return list
    .map((row, index) => ({
      id: row.id || row.month || index,
      month: row.month || row.Month || row.label || row.period || `M${index + 1}`,
      amount: Number(row.amount ?? row.Amount ?? row.commission ?? row.totalCommission ?? 0),
    }))
    .filter((row) => row.month);
};

const CommissionReport = () => {
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState({});
  const [widgets, setWidgets] = useState({});
  const [monthRows, setMonthRows] = useState([]);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: PAGE_SIZE,
    totalRecords: 0,
    totalPages: 1,
  });
  const [page, setPage] = useState(1);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [applied, setApplied] = useState({ fromDate: '', toDate: '', searchTerm: '' });

  const fetchHeader = useCallback(async ({ cancelled }) => {
    setLoading(true);
    setError('');
    try {
      const [monthRes, summaryRes, widgetsRes] = await Promise.all([
        apiService.getReportCommissionMonthwise().catch(() => ({ success: false, data: null })),
        apiService.getCommissionSummary().catch(() => ({ success: false, data: null })),
        apiService.getCommissionWidgets().catch(() => ({ success: false, data: null })),
      ]);
      if (cancelled()) return;

      const errors = [];
      if (!monthRes.success && monthRes.error && !String(monthRes.error).includes('404')) {
        errors.push(monthRes.error);
      }
      setMonthRows(normalizeMonthRows(unwrapData(monthRes) ?? monthRes));
      setSummary(unwrapData(summaryRes) || {});
      setWidgets(unwrapData(widgetsRes) || {});
      if (errors.length) setError(errors[0]);
    } catch (err) {
      if (!cancelled() && !String(err.message || '').includes('404')) {
        setError(err.message || 'Failed to load commission report');
      }
    } finally {
      if (!cancelled()) setLoading(false);
    }
  }, []);

  useFetchOnceByKey('commission-report-header', fetchHeader, true);

  const fetchHistory = useCallback(
    async ({ cancelled }) => {
      setTableLoading(true);
      try {
        const res = await apiService.getCommissionHistory({
          pageNumber: page,
          pageSize: PAGE_SIZE,
          fromDate: toApiDate(applied.fromDate),
          toDate: toApiDate(applied.toDate),
          searchTerm: applied.searchTerm.trim(),
        });
        if (cancelled()) return;
        if (!res.success && res.error && !String(res.error).includes('404')) {
          setError(res.error);
        }
        setRows(extractCommissionHistoryList(res));
        setPagination(extractCommissionHistoryPagination(res, PAGE_SIZE));
      } catch (err) {
        if (!cancelled() && !String(err.message || '').includes('404')) {
          setError(err.message || 'Failed to load commission history');
        }
        setRows([]);
      } finally {
        if (!cancelled()) setTableLoading(false);
      }
    },
    [page, applied]
  );

  useFetchOnceByKey(
    `commission-report-history|${page}|${applied.fromDate}|${applied.toDate}|${applied.searchTerm}`,
    fetchHistory,
    true
  );

  const totalCommission = Number(
    pick(summary, 'totalCommission', 'TotalCommission', 'totalEarnings', 'totalProfit') ??
      pick(widgets, 'totalCommission', 'TotalCommission', 'totalEarnings') ??
      monthRows.reduce((sum, row) => sum + row.amount, 0)
  );
  const monthlyCommission = Number(
    pick(summary, 'monthlyCommission', 'thisMonthCommission', 'currentMonthCommission') ??
      pick(widgets, 'monthlyCommission', 'thisMonthCommission') ??
      (monthRows.length ? monthRows[monthRows.length - 1].amount : 0)
  );
  const growth = Number(
    pick(widgets, 'commissionGrowth', 'CommissionGrowth', 'growthPercent', 'growth') ??
      pick(summary, 'commissionGrowth', 'growthPercent') ??
      0
  );
  const totalLots = Number(
    pick(summary, 'totalLots', 'TotalLots') ??
      pick(widgets, 'totalLots', 'TotalLots') ??
      pick(pagination, 'totalLots') ??
      0
  );

  const maxMonthAmount = useMemo(
    () => Math.max(...monthRows.map((r) => r.amount), 0),
    [monthRows]
  );

  const kpis = [
    {
      label: 'Total Commission',
      value: formatCurrency(totalCommission),
      icon: DollarSign,
      tone: 'green',
    },
    {
      label: 'This Month',
      value: formatCurrency(monthlyCommission),
      icon: Wallet,
      tone: 'blue',
    },
    {
      label: 'Commission Growth',
      value: `${growth > 0 ? '+' : ''}${growth}%`,
      icon: TrendingUp,
      tone: 'amber',
    },
    {
      label: 'Total Lots',
      value: Number(totalLots).toLocaleString('en-US', { maximumFractionDigits: 2 }),
      icon: BarChart3,
      tone: 'violet',
    },
  ];

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    setApplied({ fromDate, toDate, searchTerm });
  };

  return (
    <div className="page cr-page">
      <div className="page-inner">
        <ModulePageHeader
          icon={FileText}
          title="Commission Report"
          subtitle="Month-wise commission trend and detailed earning history"
          iconBg={`linear-gradient(135deg,${THEME},#a52a2a)`}
        />
        <ModuleAlert message={error} />

        {loading ? (
          <div className="module-card">
            <AppLoader color={THEME} />
          </div>
        ) : (
          <>
            <section className="cr-kpis">
              {kpis.map((kpi) => {
                const Icon = kpi.icon;
                return (
                  <div key={kpi.label} className={`module-card cr-kpi cr-kpi--${kpi.tone}`}>
                    <div className="cr-kpi__icon">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="cr-kpi__label">{kpi.label}</div>
                      <div className="cr-kpi__value">{kpi.value}</div>
                    </div>
                  </div>
                );
              })}
            </section>

            <section className="module-card cr-trend">
              <div className="cr-trend__head">
                <h2>Commission Trend</h2>
                <span>Month-wise</span>
              </div>
              {monthRows.length === 0 ? (
                <p className="cr-empty">No month-wise commission data available.</p>
              ) : (
                <div className="cr-bars">
                  {monthRows.map((row) => (
                    <div key={row.id} className="cr-bar-row">
                      <span className="cr-bar-row__label">{row.month}</span>
                      <div className="cr-bar-row__track">
                        <div
                          className="cr-bar-row__fill"
                          style={{
                            width: `${maxMonthAmount > 0 ? (row.amount / maxMonthAmount) * 100 : 0}%`,
                          }}
                        />
                      </div>
                      <span className="cr-bar-row__value">{formatCurrency(row.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="module-card cr-table-card">
              <div className="cr-trend__head">
                <h2>Commission History</h2>
                <span>{pagination.totalRecords || rows.length} records</span>
              </div>

              <form className="cr-filters" onSubmit={handleFilterSubmit}>
                <div className="cr-field">
                  <label htmlFor="cr-from">From</label>
                  <input
                    id="cr-from"
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                </div>
                <div className="cr-field">
                  <label htmlFor="cr-to">To</label>
                  <input
                    id="cr-to"
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </div>
                <div className="cr-field cr-field--grow">
                  <label htmlFor="cr-search">Search</label>
                  <input
                    id="cr-search"
                    type="text"
                    placeholder="Client, symbol, ticket…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <button type="submit" className="cr-btn" style={{ backgroundColor: THEME }}>
                  Apply
                </button>
              </form>

              <div className="cr-table-wrap">
                {tableLoading ? (
                  <AppLoader color={THEME} />
                ) : (
                  <table className="cr-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Client Code</th>
                        <th>Client</th>
                        <th>Symbol</th>
                        <th>Lots</th>
                        <th>Commission</th>
                        <th>Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="cr-empty-cell">
                            No commission records found.
                          </td>
                        </tr>
                      ) : (
                        rows.map((row, index) => {
                          const lots = Number(
                            row.lots ?? row.volume ?? row.lot ?? row.closedVolume ?? 0
                          );
                          return (
                            <tr key={row.id || row.ticket || row.orderId || index}>
                              <td>
                                {formatDate(
                                  row.openTime ||
                                    row.closeTime ||
                                    row.createdDate ||
                                    row.date ||
                                    row.commissionDate
                                )}
                              </td>
                              <td>{row.clientCode || row.ClientCode || row.userCode || '—'}</td>
                              <td>
                                {row.clientName ||
                                  row.fullName ||
                                  row.customerName ||
                                  row.login ||
                                  row.loginId ||
                                  '—'}
                              </td>
                              <td>{row.symbol || row.Symbol || '—'}</td>
                              <td>
                                {lots
                                  ? lots.toLocaleString('en-US', { maximumFractionDigits: 4 })
                                  : '—'}
                              </td>
                              <td className="cr-amount">
                                {formatCurrency(
                                  row.commission ??
                                    row.profit ??
                                    row.amount ??
                                    row.earning ??
                                    0
                                )}
                              </td>
                              <td>
                                {row.source ||
                                  row.commissionType ||
                                  row.type ||
                                  row.category ||
                                  '—'}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                )}
              </div>

              {pagination.totalPages > 1 ? (
                <div className="cr-pagination">
                  <button
                    type="button"
                    className="cr-page-btn"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </button>
                  <span>
                    Page {pagination.pageNumber || page} of {pagination.totalPages}
                  </span>
                  <button
                    type="button"
                    className="cr-page-btn"
                    disabled={page >= pagination.totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              ) : null}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default CommissionReport;
